import React, { useState, useReducer } from "react";

import { Search } from "icons";
import { isEmpty } from "ramda";

import icdApi from "apis/icd";
import {
  Button,
  Callout,
  Input,
  SubHeader,
  Typography,
} from "clearsense-ui/components";
import useAutoCollapse from "hooks/useAutoCollapse";

import { initialState, reducer } from "../Common/icdRxNormReducer";
import Options from "../Common/Options";
import SearchResultTable from "../Common/SearchResultTable";
import { SEARCH_CODE } from "../constants";

const Icd = () => {
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState(SEARCH_CODE);
  const [rowData, setRowData] = useState([]);

  const [state, dispatch] = useReducer(reducer, initialState);

  useAutoCollapse();

  const clearSearchValues = type => {
    if (type === "option change") {
      setSearchValue("");
    }
    setRowData([]);
    dispatch({
      type: "CLEAR_FIELDS",
    });
  };

  const handleSearch = async () => {
    clearSearchValues();
    setSearchValue(searchValue.trim());
    if (searchValue.trim()) {
      dispatch({
        type: "LOADING",
      });
      try {
        const response =
          searchType.value === SEARCH_CODE.value
            ? await icdApi.fetchIcdDescriptions(searchValue)
            : await icdApi.fetchIcdCode(searchValue);
        if (response?.data?.error) {
          dispatch({
            type: "FETCH_FAILURE",
            payload: response.data.error.message,
          });
        } else if (isEmpty(response.data)) {
          dispatch({
            type: "EMPTY_RESPONSE",
            payload: true,
          });
        } else {
          dispatch({
            type: "FETCH_SUCCESS",
            payload: response.data,
          });
        }
      } catch (error) {
        logger.error(error);
        dispatch({
          type: "FETCH_FAILURE",
          payload: "Error while fetching data",
        });
      }
    } else {
      dispatch({
        type: "FIELD_ERROR",
        payload: "Search field can't be empty",
      });
    }
  };

  return (
    <div className="cs-ui-container">
      <SubHeader
        className="mx-8 mt-8"
        breadcrumbs={[
          { label: "Reference Data", link: "#" },
          { label: "ICD 10", link: "#" },
        ]}
      />
      <div className="flex flex-col w-5/12 mx-8 mt-4 space-y-5">
        <Typography style="h3" weight="semibold">
          Search ICD 10
        </Typography>
        <Options
          setSearchType={setSearchType}
          searchType={searchType}
          clearSearchValues={clearSearchValues}
        />
        <div className="flex items-start w-full mt-4 space-x-2">
          <Input
            data-cy="icd-search-input-field"
            prefix={<Search />}
            className="rounded-sm"
            placeholder={`Enter ICD 10 ${searchType.value}`}
            size="large"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            error={state.fieldError}
          />
          <div className="h-full">
            <Button
              data-cy="icd-search-button"
              label="Search"
              size="large"
              className="h-10"
              type="submit"
              onClick={handleSearch}
            />
          </div>
        </div>
      </div>
      {!state.isResponseEmpty && !state.error && (
        <SearchResultTable
          isLoading={state.loading}
          data={state.data}
          rowData={rowData}
          setRowData={setRowData}
          searchType={searchType}
          searchValue={searchValue}
          type="ICD 10"
        />
      )}
      {state.error && (
        <Callout
          className="w-5/12 mx-auto mt-12 "
          style="danger"
          data-cy="icd-error-callout"
        >
          {state.error}
        </Callout>
      )}
      {state.isResponseEmpty && (
        <Callout
          className="w-5/12 mx-auto mt-12 "
          style="info"
          data-cy="icd-null-data-callout"
        >
          <p>
            No proper search results for the description &nbsp;
            <strong>{searchValue}</strong>
          </p>
        </Callout>
      )}
    </div>
  );
};

export default Icd;
